import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import {
    ClipboardList,
    TrendingUp,
    TrendingDown,
    Target,
    XCircle,
    Clock,
    RefreshCw,
    AlertTriangle,
    ChevronRight,
    Loader2,
    Search,
    CheckCircle2,
} from 'lucide-react';
import LivePrice from './LivePrice';
import TrackButton from './TrackButton';
import { useMyTracking } from '../../lib/useMyTracking';

const fmtPrice = (v) => {
    if (v === null || v === undefined || isNaN(v)) return '—';
    if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
    return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
};

/**
 * Sistem Önerilerim panel.
 *
 * Three persistent problems this component solves for a non-trader user:
 *   1. "The system recommended a coin and now I can't find it" — every open
 *      recommendation stays here until the horizon (TP1/TP2/SL/EXPIRED/FLIPPED).
 *   2. "Direction flipped — which one is right now?" — new signals carry a
 *      bright "ŞU AN GEÇERLİ" badge; superseded signals carry "ESKİ TAVSİYE —
 *      GEÇERSİZ" so the user always knows which stance is current.
 *   3. "I have too many recommendations, I want to find my coin" — a search
 *      input filters the list by symbol.
 */
export default function ActiveRecommendations({ onSelect, tickPrices }) {
    const [rows, setRows] = useState(null);
    const [filter, setFilter] = useState('aktif'); // 'aktif' | 'all'
    const [query, setQuery] = useState('');
    const { data: tracking } = useMyTracking();
    const trackedPredIds = useMemo(() => new Set(tracking?.tracked_prediction_ids || []), [tracking]);

    const load = useCallback(async () => {
        try {
            const { data } = await apiClient.get('/predictions/active');
            setRows(data?.predictions || []);
        } catch (e) {
            logger.warn('predictions/active failed', e);
            setRows([]);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 20000);
        return () => clearInterval(id);
    }, [load]);

    const { open, closed, filtered, flippedCount } = useMemo(() => {
        const all = rows || [];
        const open = all.filter((r) => r.status === 'open');
        const closed = all.filter((r) => r.status === 'closed');
        const flippedCount = closed.filter((r) => r.outcome === 'FLIPPED').length;
        const base = filter === 'aktif' ? open : all;
        const q = (query || '').trim().toUpperCase();
        const filtered = q
            ? base.filter((r) => r.symbol.toUpperCase().includes(q) || r.symbol.replace('USDT', '').toUpperCase().includes(q))
            : base;
        return { open, closed, filtered, flippedCount };
    }, [rows, filter, query]);

    if (rows === null) {
        return (
            <div
                data-testid="active-recs-loading"
                className="border border-slate-700 bg-slate-800 rounded-xl p-4 flex items-center gap-2 text-slate-400 text-xs"
            >
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Aktif öneriler yükleniyor…
            </div>
        );
    }

    return (
        <div
            data-testid="active-recommendations"
            className="border border-slate-700 bg-slate-800 rounded-xl overflow-hidden"
        >
            <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-3 flex-wrap">
                <ClipboardList className="h-4 w-4 text-emerald-400" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-slate-200 font-semibold">
                    Sistem Önerilerim
                </span>
                <span className="text-[10px] text-slate-400">
                    sadece <span className="text-emerald-300 font-semibold">RESMİ</span> sinyaller (güven ≥%80, R/R ≥2) · pozisyon açtıysan
                    <span className="text-emerald-300 font-semibold"> Takibe Al</span> veya Telegram'da <code className="text-emerald-300">/aldim ID</code>
                </span>
                <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                    <button
                        onClick={() => setFilter('aktif')}
                        data-testid="active-recs-filter-aktif"
                        className={`px-2 py-0.5 rounded-sm uppercase tracking-wider ${
                            filter === 'aktif'
                                ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'
                                : 'bg-slate-700 text-slate-300 border border-slate-700'
                        }`}
                    >
                        Aktif ({open.length})
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        data-testid="active-recs-filter-all"
                        className={`px-2 py-0.5 rounded-sm uppercase tracking-wider ${
                            filter === 'all'
                                ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'
                                : 'bg-slate-700 text-slate-300 border border-slate-700'
                        }`}
                    >
                        Son 6 saat ({rows.length})
                    </button>
                    {flippedCount > 0 && (
                        <span
                            data-testid="active-recs-flipped-badge"
                            title="Son 6 saatte sistemin yön değiştirdiği coinler"
                            className="px-2 py-0.5 rounded-sm uppercase tracking-wider bg-amber-500/15 text-amber-200 border border-amber-500/40 animate-pulse"
                        >
                            🔁 Yön değişen: {flippedCount}
                        </span>
                    )}
                    <button
                        onClick={load}
                        title="Yenile"
                        className="h-6 w-6 flex items-center justify-center rounded-sm text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                    >
                        <RefreshCw className="h-3 w-3" />
                    </button>
                </span>
            </div>

            {/* 🔍 Search inside the recommendations */}
            <div className="px-4 py-2 border-b border-slate-700/60 flex items-center gap-2 bg-slate-900/40">
                <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Önerilen coinler arasında ara (örn: ARB, BTC, ETH)…"
                    data-testid="active-recs-search"
                    className="flex-1 h-7 bg-transparent text-xs text-slate-100 placeholder:text-slate-500 outline-none"
                />
                {query && (
                    <button
                        onClick={() => setQuery('')}
                        data-testid="active-recs-search-clear"
                        className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-100"
                    >
                        Temizle
                    </button>
                )}
                {query && (
                    <span className="text-[10px] text-slate-400 shrink-0">
                        {filtered.length} sonuç
                    </span>
                )}
            </div>

            {filtered.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-slate-400">
                    {query
                        ? `"${query}" için öneri bulunamadı.`
                        : filter === 'aktif'
                          ? 'Şu an aktif RESMİ öneri yok. Sistem sadece güven ≥%80 ve R/R ≥2 olan, en az 3 sinyalle desteklenen fırsatları öneri olarak kaydeder. Bu sıkı filtre bilinçli: az ama sağlam sinyal.'
                          : 'Son 6 saatte öneri bulunamadı.'}
                </div>
            ) : (
                <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-700/50">
                    {filtered.map((r) => (
                        <RecommendationRow
                            key={r.id}
                            rec={r}
                            onSelect={onSelect}
                            tick={tickPrices?.[r.symbol]}
                            tracked={trackedPredIds.has(r.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

const RecommendationRow = ({ rec, onSelect, tick, tracked }) => {
    const isLong = rec.action === 'LONG';
    const Icon = isLong ? TrendingUp : TrendingDown;
    const isOpen = rec.status === 'open';
    const isCurrent = rec.is_current;
    const isFlipped = rec.outcome === 'FLIPPED';

    // Status pill color
    const outcome = rec.outcome;
    let pillClass = 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40';
    let StatusIcon = Target;
    if (!isOpen) {
        if (outcome === 'TP1' || outcome === 'TP2') {
            pillClass = 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40';
            StatusIcon = Target;
        } else if (outcome === 'SL') {
            pillClass = 'bg-rose-500/15 text-rose-200 border-rose-500/40';
            StatusIcon = XCircle;
        } else if (outcome === 'FLIPPED') {
            pillClass = 'bg-amber-500/15 text-amber-200 border-amber-500/40';
            StatusIcon = AlertTriangle;
        } else {
            pillClass = 'bg-slate-700 text-slate-200 border-slate-600';
            StatusIcon = Clock;
        }
    }

    // Verdict badge color (ŞU AN GEÇERLİ vs. ESKİ TAVSİYE)
    const verdictClass = isCurrent
        ? 'bg-emerald-500/20 text-emerald-100 border-emerald-400/50 shadow-[0_0_12px_-3px_rgba(52,211,153,0.6)]'
        : isFlipped
          ? 'bg-amber-500/15 text-amber-100 border-amber-400/40 line-through'
          : 'bg-slate-700 text-slate-300 border-slate-600';

    const shownPnl = (isOpen ? rec.pnl_pct : rec.realized_pnl_pct ?? rec.pnl_pct) ?? 0;
    const pnlColor = shownPnl > 0 ? 'text-emerald-400' : shownPnl < 0 ? 'text-rose-400' : 'text-slate-400';
    const current = tick?.price ?? rec.current_price;

    // Row visual depth: current rec gets emerald highlight, flipped rec gets dimmed amber
    const rowBg = isCurrent
        ? 'bg-emerald-950/15 hover:bg-emerald-950/25'
        : isFlipped
          ? 'bg-amber-950/15 opacity-85 hover:bg-amber-950/25'
          : 'hover:bg-slate-700/40';

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onSelect?.(rec.symbol)}
            onKeyDown={(e) => e.key === 'Enter' && onSelect?.(rec.symbol)}
            data-testid={`active-rec-${rec.symbol}-${rec.action}`}
            className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${rowBg}`}
        >
            <div className="flex items-start gap-3">
                {/* Direction icon */}
                <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
                    isLong ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'
                }`}>
                    <Icon className="h-5 w-5" />
                </div>

                <div className="min-w-0 flex-1">
                    {/* Verdict + symbol + status */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono text-sm font-semibold text-slate-100">
                            {rec.symbol.replace('USDT', '')}
                        </span>
                        {rec.short_id && (
                            <span
                                data-testid={`active-rec-id-${rec.short_id}`}
                                title="Bu önerinin kimliği — Telegram'da bu ID ile referans verebilirsin: /aldim ID"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    try {
                                        navigator.clipboard?.writeText(rec.short_id);
                                    } catch (err) { logger.warn('clipboard copy failed', err); }
                                }}
                                className="inline-flex items-center text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-900 border border-slate-600 text-slate-300 hover:text-emerald-300 hover:border-emerald-500/40 cursor-pointer"
                            >
                                #{rec.short_id}
                            </span>
                        )}
                        {rec.kind_tr && (
                            <span
                                data-testid={`active-rec-kind-${rec.short_id}`}
                                title="Favori radar planının tetiği hacimle doldu; bu kayıt sistem istatistiğine girmez, sadece takip için"
                                className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-200"
                            >
                                {rec.kind_tr}
                            </span>
                        )}
                        <span
                            data-testid={`active-rec-verdict-${rec.symbol}`}
                            className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${verdictClass}`}
                        >
                            {isCurrent && <CheckCircle2 className="h-3 w-3" />}
                            {rec.verdict_tr}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${pillClass}`}>
                            <StatusIcon className="h-3 w-3" />
                            {rec.status_tr}
                        </span>
                        <span className="text-[10px] text-slate-400 ml-auto">
                            {rec.age_tr}
                            {rec.duration_tr && ` · ${rec.duration_tr}`}
                        </span>
                    </div>

                    {rec.reason_tr && (
                        <div className={`text-[11px] leading-snug mb-1.5 ${
                            isFlipped ? 'text-amber-200/90' : 'text-slate-300'
                        }`}>
                            {rec.reason_tr}
                        </div>
                    )}

                    {/* Prices + P&L in a tight grid */}
                    <div className="grid grid-cols-4 gap-2 text-[10px]">
                        <div>
                            <div className="text-slate-400 uppercase tracking-wider text-[9px]">Giriş</div>
                            <div className="font-mono text-slate-100">${fmtPrice(rec.entry)}</div>
                        </div>
                        <div>
                            <div className="text-slate-400 uppercase tracking-wider text-[9px]">
                                {isOpen ? 'Şu an' : 'Kapanış'}
                            </div>
                            <div className="font-mono text-slate-100">
                                <LivePrice symbol={rec.symbol} tick={tick} fallbackPrice={current} className="text-[11px]" />
                            </div>
                        </div>
                        <div>
                            <div className="text-slate-400 uppercase tracking-wider text-[9px]">Hedef</div>
                            <div className={`font-mono ${isLong ? 'text-emerald-300' : 'text-rose-300'}`}>
                                ${fmtPrice(rec.tp2)}
                            </div>
                        </div>
                        <div>
                            <div className="text-slate-400 uppercase tracking-wider text-[9px]">{isOpen ? 'K/Z' : 'Plan sonucu'}</div>
                            <div className={`font-mono font-semibold ${pnlColor}`} data-testid={`active-rec-pnl-${rec.short_id}`}>
                                {shownPnl >= 0 ? '+' : ''}{shownPnl?.toFixed(2)}%
                            </div>
                        </div>
                    </div>

                    {isOpen && (
                        <div className="mt-1.5 text-[10px] text-slate-400 flex items-center gap-2 flex-wrap">
                            <span>Stop: <span className="font-mono text-amber-300">${fmtPrice(rec.stop_loss)}</span></span>
                            <span>·</span>
                            <span>R/R: <span className="font-mono text-slate-200">{rec.rr_ratio?.toFixed(2)}</span></span>
                            <span>·</span>
                            <span>güven %{rec.confidence}</span>
                            <span className="ml-auto" />
                            <TrackButton shortId={rec.short_id} tracked={tracked} defaultLeverage={rec.leverage || 1} testid={`active-rec-track-${rec.short_id}`} />
                            {rec.short_id && (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        try {
                                            navigator.clipboard?.writeText(`/aldim ${rec.short_id}`);
                                        } catch (err) { logger.warn('clipboard copy failed', err); }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            try {
                                                navigator.clipboard?.writeText(`/aldim ${rec.short_id}`);
                                            } catch (err) { logger.warn('clipboard copy failed', err); }
                                        }
                                    }}
                                    data-testid={`active-rec-copy-aldim-${rec.short_id}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-medium text-sky-300 hover:text-sky-200 bg-sky-500/10 border border-sky-500/30 rounded-sm px-2 py-0.5 cursor-pointer"
                                    title="Telegram'a yapıştırmak için komutu kopyala"
                                >
                                    📋 /aldim {rec.short_id}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <ChevronRight className="h-4 w-4 text-slate-500 self-center shrink-0" />
            </div>
        </div>
    );
};
